// import { canvasDeltaToPdfDelta } from "../../../../utils/coordinateUtils";

const COPYABLE_TYPES = new Set([
  "PDFTextField",
  "PDFCheckBox",
  "PDFRadioGroup",
  "PDFText",
  "PDFRectangle",
  "PDFLine",
]);

// Keep signature ordering and section markers out of ordinary field copying.
const RESERVED_NAME =
  /^(?:notary_seal_[ab]|notary_sign_[ab]|sign_[abc])(?:_|$)|^block__/;

const RESET_KEYS = [
  "id",
  "_id",
  "field_id",
  "field_instance_id",
  "parent_field_id",
  "block_id",
  "block_label",
  "category_id",
  "location",
];

const clipboards = new WeakMap();
const handledEvents = new WeakSet();
let sequence = 0;

const clone = (value) => JSON.parse(JSON.stringify(value));

const pageOf = (field) => Number(field.page_num ?? field.page ?? 1);

export const getSelectedCopyableFields = (tempFields) => {
  const page = Number(tempFields.currentDocument?.pageNumber ?? 1);

  const byName = new Map(
    (tempFields.formFields || []).map((field) => [field.name, field]),
  );

  const names = new Set([
    tempFields.selectedFormField?.name,
    ...(tempFields.selectedExtraFields || []).map((field) => field?.name),
  ]);

  // Use live field records so copying after moving uses the latest position.
  return [...names]
    .map((name) => byName.get(name))
    .filter(
      (field) =>
        field &&
        COPYABLE_TYPES.has(field.type) &&
        !RESERVED_NAME.test(field.name) &&
        pageOf(field) === page &&
        [field.x, field.y, field.width, field.height].every(Number.isFinite),
    );
};

export const buildPastedFields = (
  copiedFields,
  tempFields,
  pasteNumber,
  batchId,
) => {
  if (!copiedFields.length) return [];

  const doc = tempFields.currentDocument || {};
  const page = Number(doc.pageNumber ?? 1);

  // const offset = canvasDeltaToPdfDelta(
  //   12 * pasteNumber,
  //   12 * pasteNumber,
  //   doc.viewport,
  //   doc.scaleFactor,
  //   true,
  // );

  // Move the whole group by the same amount to preserve spacing.
  // const minX = Math.min(...copiedFields.map((field) => field.x));
  // const minY = Math.min(...copiedFields.map((field) => field.y));

  // const maxX = Math.max(...copiedFields.map((field) => field.x + field.width));

  // const maxY = Math.max(...copiedFields.map((field) => field.y + field.height));

  // const clamp = (delta, min, max, origin, size) =>
  //   Number.isFinite(size) && size > 0
  //     ? Math.max(origin - min, Math.min(delta, origin + size - max))
  //     : delta;

  // const dx = clamp(offset.x, minX, maxX, doc.pageXMin ?? 0, doc.actualWidth);

  // const dy = clamp(offset.y, minY, maxY, doc.pageYMin ?? 0, doc.actualHeight);

  const usedNames = new Set(
    (tempFields.formFields || []).map((field) => field.name),
  );

  const maxOrder = Math.max(
    0,
    ...(tempFields.formFields || []).map((field) => Number(field.order) || 0),
  );

  return copiedFields.map((source, index) => {
    const field = clone(source);

    RESET_KEYS.forEach((key) => delete field[key]);

    const prefix = source.name.startsWith("date_field")
      ? "date_field"
      : {
          PDFTextField: "textField",
          PDFCheckBox: "checkbox",
          PDFRadioGroup: "radio",
          PDFText: "insertText",
          PDFRectangle: "rectangle",
          PDFLine: "line",
        }[source.type];

    let name = `${prefix}_${batchId}_${index}`;

    while (usedNames.has(name)) {
      name += "_copy";
    }

    usedNames.add(name);

    return {
      ...field,
      name,
      x: source.x,
      y: source.y,
      ...(field.rect && !Array.isArray(field.rect)
        ? {
            rect: {
              ...field.rect,
              X: source.x,
              Y: source.y,
            },
          }
        : {}),
      page,
      page_num: page,
      order: maxOrder + index + 1,
    };
  });
};

export const handleFieldClipboardShortcut = (
  event,
  currentProcessingFile,
  setCurrentProcessingFile,
  typingToolActive,
) => {
  const key = event.key?.toLowerCase();

  if (
    !(event.ctrlKey || event.metaKey) ||
    event.altKey ||
    event.shiftKey ||
    !["c", "v"].includes(key)
  ) {
    return false;
  }

  // Prevent a React handler and document listener handling the same event twice.
  const nativeEvent = event.nativeEvent || event;

  if (handledEvents.has(nativeEvent)) return true;
  if (event.defaultPrevented) return false;

  const target = event.target;
  const tag = target?.tagName;

  const typing =
    typingToolActive ||
    target?.isContentEditable ||
    tag === "TEXTAREA" ||
    tag === "SELECT" ||
    (tag === "INPUT" && !target.readOnly);

  if (typing) return false;

  const tempFields = currentProcessingFile?.document?.tempFields;

  if (!tempFields?.currentDocument?.base64File) return false;

  if (key === "c") {
    const fields = getSelectedCopyableFields(tempFields);

    if (!fields.length) return false;

    clipboards.set(setCurrentProcessingFile, {
      fields: clone(fields),
      count: 0,
    });
  } else {
    const clipboard = clipboards.get(setCurrentProcessingFile);

    if (!clipboard?.fields.length) return false;

    const pasteNumber = ++clipboard.count;
    const batchId = `${Date.now().toString(36)}_${++sequence}`;
    const copiedFields = clipboard.fields;

    setCurrentProcessingFile((prevFile) => {
      const previous = prevFile?.document?.tempFields;

      if (!previous?.currentDocument?.base64File) return prevFile;

      const pasted = buildPastedFields(
        copiedFields,
        previous,
        pasteNumber,
        batchId,
      );

      return {
        ...prevFile,
        document: {
          ...prevFile.document,
          tempFields: {
            ...previous,
            formFields: [...(previous.formFields || []), ...pasted],
            selectedFormField: pasted[0],
            selectedExtraFields: pasted,
            selectedPdfTextItem: null,
            selectedExtraPdfTextItems: [],
            formFieldTools: Object.fromEntries(
              Object.entries(previous.formFieldTools || {}).map(
                ([name, tool]) => [
                  name,
                  {
                    ...tool,
                    active: false,
                    values: [],
                  },
                ],
              ),
            ),
          },
        },
      };
    });
  }

  handledEvents.add(nativeEvent);
  event.preventDefault();
  event.stopPropagation();

  return true;
};
